/** Longest wait for the examiner recording before the browser voice asks instead. */
export const URL_WAIT_MS = 8000;
/** Longest wait for the recording to start loading once its URL is known. */
export const LOAD_WAIT_MS = 10000;

/** A WAV with no samples: playing it costs nothing and counts as the page having played sound. */
const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';

/**
 * Lets the examiner be heard without a tap. Phone browsers allow sound only from code that runs inside a tap, and
 * the first question is asked after the microphone prompt and a download, which is too late; the "Repeat" button
 * worked only because it is a tap itself. Call this synchronously from the Start tap, with the element that will
 * later play the questions: once it has played something, it and the browser voice may play on their own.
 */
export function unlockAudio(audio: HTMLAudioElement): void {
  audio.src = SILENT_WAV;
  Promise.resolve(audio.play()).catch(() => undefined);
  const synth = typeof window !== 'undefined' ? window.speechSynthesis : undefined;
  if (synth && typeof SpeechSynthesisUtterance !== 'undefined') {
    const primer = new SpeechSynthesisUtterance('');
    primer.volume = 0;
    synth.speak(primer);
  }
}

/** Reads a question aloud with the browser's voice; calls `onEnd` once, even if the voice never reports back. */
export function speak(text: string, onEnd: () => void): () => void {
  let finished = false;
  const done = () => {
    if (finished) return;
    finished = true;
    onEnd();
  };
  const synth = typeof window !== 'undefined' ? window.speechSynthesis : undefined;
  if (!synth || typeof SpeechSynthesisUtterance === 'undefined') {
    done();
    return () => undefined;
  }
  synth.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'en-GB';
  utterance.rate = 0.95;
  const voice = synth.getVoices().find((v) => v.lang === 'en-GB') ?? synth.getVoices().find((v) => v.lang.startsWith('en'));
  if (voice) utterance.voice = voice;
  utterance.onend = done;
  utterance.onerror = done;
  // Some browsers never fire `end`; do not leave the candidate waiting.
  const fallback = setTimeout(done, 3000 + text.length * 90);
  synth.speak(utterance);
  return () => {
    clearTimeout(fallback);
    finished = true;
    synth.cancel();
  };
}

/**
 * Asks one question in the examiner's recorded voice. When there is no
 * recording, or it is slow, fails or is blocked, the browser's voice asks
 * instead. Calls `onEnd` once whatever happens; returns a cancel function.
 */
export function askAloud(audio: HTMLAudioElement, url: Promise<string | null>, text: string, onEnd: () => void): () => void {
  let state: 'waiting' | 'playing' | 'speaking' | 'over' = 'waiting';
  let stopSpeech: () => void = () => undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const detach = () => {
    clearTimeout(timer);
    audio.onended = null;
    audio.onerror = null;
    audio.onloadedmetadata = null;
  };
  const finish = () => {
    if (state === 'over') return;
    state = 'over';
    detach();
    onEnd();
  };
  const switchToBrowserVoice = () => {
    if (state !== 'waiting' && state !== 'playing') return;
    detach();
    audio.pause();
    state = 'speaking';
    stopSpeech = speak(text, finish);
  };
  const play = (src: string | null) => {
    if (state !== 'waiting') return;
    if (!src) {
      switchToBrowserVoice();
      return;
    }
    clearTimeout(timer);
    state = 'playing';
    audio.onended = finish;
    audio.onerror = switchToBrowserVoice;
    timer = setTimeout(switchToBrowserVoice, LOAD_WAIT_MS);
    // A download that stalls mid-question must not leave the candidate waiting either.
    audio.onloadedmetadata = () => {
      clearTimeout(timer);
      if (Number.isFinite(audio.duration)) timer = setTimeout(finish, (audio.duration + 5) * 1000);
    };
    audio.src = src;
    Promise.resolve(audio.play()).catch(switchToBrowserVoice);
  };

  timer = setTimeout(switchToBrowserVoice, URL_WAIT_MS);
  url.then(play, switchToBrowserVoice);

  return () => {
    const speaking = state === 'speaking';
    state = 'over';
    detach();
    audio.pause();
    if (speaking) stopSpeech();
  };
}
